import { Slot } from '@radix-ui/react-slot'
import { cn } from '@workspace/ui/lib/utils'
import { cva, type VariantProps } from 'class-variance-authority'
import type * as React from 'react'

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-60 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-[rgb(249,115,22)] focus-visible:ring-[rgb(249,115,22)]/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive uppercase",
  {
    variants: {
      variant: {
        default: 'bg-[#f97316] text-white shadow-xs hover:bg-[#fb923c]',
        destructive:
          'bg-red-600 text-white shadow-xs hover:bg-red-500 focus-visible:ring-red-500/20',
        outline:
          'border border-border bg-input text-white shadow-xs hover:bg-hover hover:text-white',
        'outline-primary': 'border border-[#f97316]/40 text-[#f97316] hover:bg-[#f97316]/10',
        secondary: 'bg-secondary text-white shadow-xs hover:bg-hover',
        ghost: 'text-white hover:bg-secondary hover:text-white',
        link: 'text-[#f97316] underline-offset-4 hover:underline',
      },
      size: {
        sm: 'h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5 text-xs',
        md: 'h-10 px-4 py-2 has-[>svg]:px-3',
        lg: 'h-14 rounded-md px-9 has-[>svg]:px-6 text-xl',
        icon: 'size-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'md',
    },
  }
)

function Button({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }) {
  const Comp = asChild ? Slot : 'button'

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  )
}

export { Button, buttonVariants }
